import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  alpha,
  useTheme,
  Tooltip,
  LinearProgress,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Edit2,
  Wifi,
  RefreshCw,
  Linkedin,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Shield,
  X,
} from "lucide-react";
import {
  fetchLIAccounts,
  createLIAccount,
  deleteLIAccount,
  testLIAccountConnection,
  refreshLIAccountCookies,
} from "../../../../../../redux/actions/linkedinOutreachAction";

// Locations are fetched from backend API

const STATUS_CONFIG = {
  active: { label: "Active", color: "success" },
  warming_up: { label: "Warming Up", color: "info" },
  paused: { label: "Paused", color: "warning" },
  cookie_expired: { label: "Cookie Expired", color: "error" },
  verification_needed: { label: "Verification Needed", color: "warning" },
  banned: { label: "Banned", color: "error" },
};

const getHealthColor = (score) => {
  if (score >= 70) return "#10b981";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
};

const AccountCard = ({ account, theme, onTest, onRefresh, onEdit, onDelete, onSubmitCode, onConfirmApp }) => {
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [submittingCode, setSubmittingCode] = useState(false);
  const [confirmingApp, setConfirmingApp] = useState(false);

  // Clear "Verifying with LinkedIn…" when status flips. Two terminal cases:
  //   - "active" → login succeeded
  //   - "cookie_expired" → login failed, user needs to retry
  // Without this, the spinner would show forever after a failed login.
  // We also use a 60s safety timeout so the UI never hangs even if status
  // somehow stays at verification_needed (e.g. network blip during polling).
  const submittingTimeoutRef = useRef(null);
  useEffect(() => {
    if (submittingCode && account.status !== "verification_needed") {
      setSubmittingCode(false);
      if (submittingTimeoutRef.current) {
        clearTimeout(submittingTimeoutRef.current);
        submittingTimeoutRef.current = null;
      }
    }
  }, [account.status, submittingCode]);
  useEffect(() => {
    if (submittingCode && !submittingTimeoutRef.current) {
      submittingTimeoutRef.current = setTimeout(() => {
        setSubmittingCode(false);
        submittingTimeoutRef.current = null;
      }, 60000);
    }
    return () => {
      if (!submittingCode && submittingTimeoutRef.current) {
        clearTimeout(submittingTimeoutRef.current);
        submittingTimeoutRef.current = null;
      }
    };
  }, [submittingCode]);

  const statusCfg = STATUS_CONFIG[account.status] || { label: account.status, color: "default" };
  const healthScore = Math.round((account.health_score ?? 0) * 100);
  const healthColor = getHealthColor(healthScore);

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest(account.id);
    } finally {
      setTesting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh(account.id);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      {/* Header: email + status */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#0A66C2", 0.1),
              flexShrink: 0,
            }}
          >
            <Linkedin size={18} color="#0A66C2" strokeWidth={1.75} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.9rem",
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {account.linkedin_email}
          </Typography>
        </Box>
        <Chip
          label={statusCfg.label}
          color={statusCfg.color}
          size="small"
          sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24, borderRadius: "6px" }}
        />
      </Box>

      {/* Health score */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            Health Score
          </Typography>
          <Typography variant="caption" sx={{ color: healthColor, fontWeight: 600 }}>
            {healthScore}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={healthScore}
          sx={{
            height: 6,
            borderRadius: "3px",
            bgcolor: alpha(healthColor, 0.12),
            "& .MuiLinearProgress-bar": {
              borderRadius: "3px",
              bgcolor: healthColor,
            },
          }}
        />
      </Box>

      {/* Details row */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        {account.proxy_geo && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <MapPin size={13} color={theme.palette.text.secondary} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {account.proxy_geo}
            </Typography>
          </Box>
        )}
        {account.timezone && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Clock size={13} color={theme.palette.text.secondary} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {account.timezone}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Daily stats */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          p: 1.5,
          borderRadius: "8px",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Send size={13} color={theme.palette.text.secondary} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Invites today:{" "}
            <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {account.invites_sent_today ?? 0}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <MessageSquare size={13} color={theme.palette.text.secondary} />
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Messages today:{" "}
            <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {account.messages_sent_today ?? 0}
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Verification UI — adapts based on:
            (1) verification_type (code email vs app confirm)
            (2) refresh_job_active — is a Playwright pod actually polling for our input?
            (3) submittingCode — has the user just clicked Verify? show "Verifying…" until status flips */}
      {account.status === "verification_needed" && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "8px",
            bgcolor: alpha("#f59e0b", 0.08),
            border: `1px solid ${alpha("#f59e0b", 0.3)}`,
          }}
        >
          {/* Session expired: the previous refresh Job timed out (5-min poll
              window). Submitting a code now would land in the DB with no
              consumer — direct the user to Refresh instead. */}
          {!account.refresh_job_active && !submittingCode && !confirmingApp ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#f59e0b", display: "block", mb: 0.5 }}>
                Verification session expired
              </Typography>
              <Typography variant="caption" sx={{ color: alpha("#f59e0b", 0.85), display: "block", mb: 1.5 }}>
                The previous login attempt timed out. Click <b>Refresh</b> below to start a new session — LinkedIn will issue a fresh code.
              </Typography>
              <Button
                size="small"
                variant="contained"
                disabled={refreshing}
                onClick={() => {
                  setRefreshing(true);
                  onRefresh(account.id).finally(() => setRefreshing(false));
                }}
                startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, minWidth: 140 }}
              >
                {refreshing ? "Starting…" : "Start new session"}
              </Button>
            </>
          ) : submittingCode ? (
            // Persistent loading state: backend accepted the code, now we're
            // waiting for the Playwright pod to submit it to LinkedIn and
            // for the polling loop to flip status to "active".
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <CircularProgress size={14} sx={{ color: "#f59e0b" }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#f59e0b" }}>
                  Verifying with LinkedIn…
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: alpha("#f59e0b", 0.85), display: "block" }}>
                This usually takes 10–30 seconds. The form will close automatically when the login completes.
              </Typography>
            </>
          ) : account.verification_type === "app_confirm" ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#f59e0b", display: "block", mb: 0.5 }}>
                LinkedIn requires app verification
              </Typography>
              <Typography variant="caption" sx={{ color: alpha("#f59e0b", 0.85), display: "block", mb: 1.5 }}>
                Open the LinkedIn app on your phone and tap <b>Yes</b> to confirm the sign-in. Then click the button below.
              </Typography>
              <Button
                size="small"
                variant="contained"
                disabled={confirmingApp}
                onClick={() => {
                  setConfirmingApp(true);
                  onConfirmApp(account.id).finally(() => setConfirmingApp(false));
                }}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, minWidth: 120 }}
              >
                {confirmingApp ? <CircularProgress size={14} color="inherit" /> : "I've confirmed on the app"}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#f59e0b", display: "block", mb: 1 }}>
                LinkedIn sent a verification code to your email. Enter it below:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Enter code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && verifyCode.trim()) {
                      setSubmittingCode(true);
                      onSubmitCode(account.id, verifyCode.trim())
                        .then(() => {
                          setVerifyCode("");
                          // Keep submittingCode=true; resets when status changes (effect below).
                        })
                        .catch(() => {
                          setSubmittingCode(false);
                        });
                    }
                  }}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.85rem" } }}
                  autoFocus
                />
                <Button
                  size="small"
                  variant="contained"
                  disabled={!verifyCode.trim() || submittingCode}
                  onClick={() => {
                    setSubmittingCode(true);
                    onSubmitCode(account.id, verifyCode.trim())
                      .then(() => {
                        setVerifyCode("");
                      })
                      .catch(() => {
                        setSubmittingCode(false);
                      });
                  }}
                  sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, minWidth: 80 }}
                >
                  {submittingCode ? <CircularProgress size={14} color="inherit" /> : "Verify"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Tooltip title="Test Connection">
          <span>
            <IconButton
              size="small"
              onClick={handleTest}
              disabled={testing}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                width: 32,
                height: 32,
              }}
            >
              {testing ? <CircularProgress size={14} /> : <Wifi size={14} />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refresh Cookies">
          <span>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                width: 32,
                height: 32,
              }}
            >
              {refreshing ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => onEdit(account)}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              width: 32,
              height: 32,
            }}
          >
            <Edit2 size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(account)}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              width: 32,
              height: 32,
              color: theme.palette.error.main,
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) },
            }}
          >
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

const AccountList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { accounts, loading } = useSelector((state) => state.linkedinOutreach);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  // {severity: "error"|"info"|"success", message: string} | null
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    linkedin_email: "",
    linkedin_password: "",
    location: "ma-casablanca",
  });

  useEffect(() => {
    dispatch(fetchLIAccounts());
    // Fetch available proxy locations
    import("../../../../../../api/backend").then(({ backendClient }) => {
      backendClient.getLIProxyLocations().then(setLocations).catch(() => {});
    });
  }, [dispatch]);

  // Continuous polling: refresh accounts every 3s when any account is in a transitional state
  // (cookie_expired after clicking refresh, or verification_needed waiting for code)
  const accountList = Array.isArray(accounts) ? accounts : [];
  const needsPolling = accountList.some(
    (a) => a.status === "verification_needed" || a.status === "cookie_expired"
  );

  useEffect(() => {
    if (!needsPolling) return;
    const interval = setInterval(() => {
      dispatch(fetchLIAccounts());
    }, 3000);
    return () => clearInterval(interval);
  }, [needsPolling, dispatch]);

  const resetForm = () => {
    setFormData({
      linkedin_email: "",
      linkedin_password: "",
      location: "ma-casablanca",
    });
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await dispatch(createLIAccount(formData));
      setCreateOpen(false);
      resetForm();
    } catch (e) {
      // error handled by redux
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteLIAccount(deleteTarget.id));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleTest = async (id) => {
    await dispatch(testLIAccountConnection(id));
    dispatch(fetchLIAccounts());
  };

  const handleRefresh = async (id) => {
    await dispatch(refreshLIAccountCookies(id));
    // Polling is handled by the useEffect above — it auto-starts when status is transitional
    dispatch(fetchLIAccounts());
  };

  const handleSubmitCode = async (id, code) => {
    const { backendClient } = await import("../../../../../../api/backend");
    try {
      await backendClient.submitLIVerificationCode(id, code);
      // Polling continues via useEffect until status changes to active
      dispatch(fetchLIAccounts());
    } catch (e) {
      // Surface 409 "no_active_session" as a clear actionable toast.
      // Re-throw so the AccountCard can also clear its loading state.
      if (e && e.errorCode === "no_active_session") {
        setToast({
          severity: "warning",
          message: "Verification session expired. Click Refresh below to start a new one.",
        });
      } else {
        setToast({
          severity: "error",
          message: e?.message || "Failed to submit verification code.",
        });
      }
      dispatch(fetchLIAccounts());
      throw e;
    }
  };

  const handleConfirmApp = async (id) => {
    const { backendClient } = await import("../../../../../../api/backend");
    try {
      await backendClient.confirmLIAppVerification(id);
      dispatch(fetchLIAccounts());
    } catch (e) {
      if (e && e.errorCode === "no_active_session") {
        setToast({
          severity: "warning",
          message: "Verification session expired. Click Refresh below to start a new one.",
        });
      } else {
        setToast({
          severity: "error",
          message: e?.message || "Failed to confirm app verification.",
        });
      }
      dispatch(fetchLIAccounts());
      throw e;
    }
  };

  const handleEdit = (account) => {
    setFormData({
      linkedin_email: account.linkedin_email || "",
      linkedin_password: "",
      location: "ma-casablanca",
    });
    setCreateOpen(true);
  };

  if (loading && (!accounts || accounts.length === 0)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#0A66C2", 0.1),
            }}
          >
            <Linkedin size={20} color="#0A66C2" strokeWidth={1.75} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.05rem" }}>
            LinkedIn Accounts
          </Typography>
          <Chip
            label={accountList.length}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.72rem",
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setCreateOpen(true);
          }}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "0.8rem",
            px: 2,
          }}
        >
          Add Account
        </Button>
      </Box>

      {/* Account cards grid */}
      {accountList.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Shield size={40} color={theme.palette.text.disabled} strokeWidth={1.25} />
          <Typography sx={{ mt: 2, color: theme.palette.text.secondary, fontWeight: 500 }}>
            No LinkedIn accounts configured yet
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: "block", mt: 0.5 }}>
            Add your first account to start outreach campaigns
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => {
              resetForm();
              setCreateOpen(true);
            }}
            sx={{ mt: 2.5, textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
          >
            Add Account
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {accountList.map((account) => (
            <Grid item xs={12} sm={6} lg={4} key={account.id}>
              <AccountCard
                account={account}
                theme={theme}
                onTest={handleTest}
                onRefresh={handleRefresh}
                onEdit={handleEdit}
                onDelete={setDeleteTarget}
                onSubmitCode={handleSubmitCode}
                onConfirmApp={handleConfirmApp}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Account Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            fontSize: "1rem",
            pb: 1,
          }}
        >
          Add LinkedIn Account
          <IconButton size="small" onClick={() => setCreateOpen(false)}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="LinkedIn Email"
              value={formData.linkedin_email}
              onChange={(e) => setFormData({ ...formData, linkedin_email: e.target.value })}
              size="small"
              fullWidth
              required
              autoComplete="off"
              InputProps={{
                sx: { borderRadius: "8px", fontSize: "0.875rem" },
              }}
            />
            <TextField
              label="LinkedIn Password"
              type="password"
              value={formData.linkedin_password}
              onChange={(e) => setFormData({ ...formData, linkedin_password: e.target.value })}
              size="small"
              fullWidth
              required
              autoComplete="new-password"
              InputProps={{
                sx: { borderRadius: "8px", fontSize: "0.875rem" },
              }}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel sx={{ fontSize: "0.875rem" }}>Location</InputLabel>
              <Select
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                label="Location"
                sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.key} value={loc.key} sx={{ fontSize: "0.85rem" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                      <span>{loc.label}</span>
                      <Typography variant="caption" sx={{ color: "text.secondary", ml: 2 }}>
                        {loc.timezone}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            size="small"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 500, color: theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !formData.linkedin_email || !formData.linkedin_password || !formData.location}
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Plus size={14} />}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, px: 2 }}
          >
            {saving ? "Adding..." : "Add Account"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem" }}>
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to delete the account{" "}
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {deleteTarget?.linkedin_email}
            </Typography>
            ? This action cannot be undone. All campaigns and data associated with this account will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            size="small"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 500, color: theme.palette.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            size="small"
            startIcon={<Trash2 size={14} />}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, px: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast for verification errors (e.g. session expired 409) */}
      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ borderRadius: "8px" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AccountList;
